// Share-a-dashboard modal.
//
// Type-and-search input box against real Cognito users (locked decision,
// ai-chat-dashboard-tracker.md open question #2): as the owner types, it
// searches/autocompletes; if the typed value doesn't match any real
// eligible user, shows an inline "User does not exist" message rather
// than letting the share go through. Share targets are restricted to
// amazon-internal/admins server-side (services/cognito_users.py) — this
// component just reflects whatever the search endpoint returns.

import React, { useState, useRef } from 'react';
import { Modal, Box, SpaceBetween, Button, Autosuggest, Alert, Badge } from '@cloudscape-design/components';
import { dashboardsApi } from './dashboardsApi.js';

export default function ShareDashboardModal({ dashboardId, shares, onDismiss, onShared, onRevoked }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [error, setError] = useState('');
  const [sharing, setSharing] = useState(false);
  const searchTimer = useRef(null);

  const handleInputChange = (value) => {
    setQuery(value);
    setError('');
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (!value || value.length < 2) {
        setOptions([]);
        return;
      }
      dashboardsApi.searchShareTargets(value)
        .then((data) => setOptions((data.users || []).map((u) => ({ label: `${u.alias} (${u.email})`, value: u.alias }))))
        .catch(() => setOptions([]));
    }, 300);
  };

  const handleShare = async () => {
    const alias = query.trim().split(' ')[0]; // in case user typed the full "alias (email)" label
    if (!alias) return;
    setSharing(true);
    setError('');
    try {
      await dashboardsApi.shareDashboard(dashboardId, alias);
      setQuery('');
      setOptions([]);
      onShared();
    } catch (e) {
      setError(e.message || 'User does not exist.');
    } finally {
      setSharing(false);
    }
  };

  const handleRevoke = async (username) => {
    try {
      await dashboardsApi.revokeShare(dashboardId, username);
      onRevoked();
    } catch (e) {
      setError(e.message || 'Failed to revoke share.');
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Share dashboard"
      footer={
        <Box float="right">
          <Button variant="link" onClick={onDismiss}>Close</Button>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <SpaceBetween size="xs">
          <Autosuggest
            value={query}
            onChange={({ detail }) => handleInputChange(detail.value)}
            onSelect={({ detail }) => setQuery(detail.selectedOption?.value || detail.value)}
            options={options}
            placeholder="Type a username to search…"
            empty="No matching users"
            enteredTextLabel={(v) => v}
          />
          {error && <Alert type="error">{error}</Alert>}
          <Button variant="primary" onClick={handleShare} loading={sharing} disabled={!query.trim()}>
            Share
          </Button>
        </SpaceBetween>

        <Box>
          <Box variant="awsui-key-label">Currently shared with</Box>
          {(!shares || shares.length === 0) && <Box color="text-status-inactive">Not shared with anyone yet.</Box>}
          <SpaceBetween direction="horizontal" size="xs">
            {shares && shares.map((s) => (
              <span key={s.username} className="ai-chat-share-pill">
                <Badge color="blue">{s.username}</Badge>
                <button className="ai-chat-share-revoke" onClick={() => handleRevoke(s.username)} title="Revoke access">✕</button>
              </span>
            ))}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Modal>
  );
}
